import { DEFAULT_WALL_COLOR } from "@gettactic/map-builder/src/components/map/defines";
import type { WallPathShape } from "@gettactic/map-builder/src/types";
import {
  computeWallPathBounds,
  createWallPath
} from "@gettactic/map-builder/src/utils/shapeFactory";
import {
  snapToWallEndpoint,
  type WallSnapResult
} from "@gettactic/map-builder/src/utils/snap";
import { splitWallAtPoint } from "@gettactic/map-builder/src/utils/wallAttach";
import {
  addShapes,
  type DocumentState,
  updateShape
} from "../document/operations";
import { documentFromShapes, orderedShapes } from "../document/serialize";
import { isPrimaryButton } from "../gesture/pointer";
import type { Point } from "../types";
import { createFrameScheduler, type Tool, type ToolContext } from "./Tool";
import {
  constrainAngle,
  segmentReadout,
  softConstrainAngle
} from "./wallDrawing";

const CLOSE_THRESHOLD_PX = 15;

const splitHost = (
  document: DocumentState,
  hit: NonNullable<WallSnapResult["segment"]> & Point
): DocumentState => {
  const list = orderedShapes(document);
  const next = splitWallAtPoint(list, hit);
  return next === list ? document : documentFromShapes(next);
};

/**
 * Click to add vertices; the path is created on the second click and grows
 * with each one after that, so it is on the canvas (and undoable) while it
 * is drawn. Clicking the first vertex closes it; double-click, right-click,
 * Enter or Escape stop. Ends snap to other walls (Alt bypasses), Shift
 * constrains the angle. Same behaviour as the Konva editor's wall-path tool.
 */
export const createWallPathTool = (): Tool => {
  let lastVertex: Point | null = null;
  let activeShapeId: string | null = null;
  let context: ToolContext | null = null;

  /** The first vertex of the path being drawn, when a click near it
   *  would close the path. */
  const closingVertex = (toolContext: ToolContext, point: Point) => {
    if (!activeShapeId) return null;
    const state = toolContext.store.getState();
    const active = state.shapes[activeShapeId];
    if (active?.type !== "wall-path" || active.vertices.length < 2) return null;
    const first = active.vertices[0];
    const threshold = CLOSE_THRESHOLD_PX / Math.max(state.viewport.scale, 0.1);
    return Math.hypot(point.x - first.x, point.y - first.y) < threshold
      ? first
      : null;
  };

  const snapCandidate = (
    toolContext: ToolContext,
    client: Point,
    shiftKey: boolean,
    altKey: boolean
  ): WallSnapResult & { isConstrained: boolean; closeAt: Point | null } => {
    const pointer = toolContext.toWorld(client);
    const raw = toolContext.snap.snapPoint(pointer);
    // Shift locks the angle; otherwise a nearly straight segment is
    // straightened, so walls come out square without holding a key.
    const constrained = !lastVertex
      ? { point: raw, isConstrained: false }
      : shiftKey
        ? { point: constrainAngle(lastVertex, raw), isConstrained: true }
        : softConstrainAngle(lastVertex, raw);
    const candidate = constrained.point;
    // Closing goes by where the pointer really is, before the grid moves it.
    const closeAt = altKey ? null : closingVertex(toolContext, pointer);
    if (closeAt) {
      return { ...closeAt, snapped: true, isConstrained: false, closeAt };
    }
    if (altKey) {
      return {
        ...candidate,
        snapped: false,
        isConstrained: constrained.isConstrained,
        closeAt: null
      };
    }
    const state = toolContext.store.getState();
    const snapped = snapToWallEndpoint(
      candidate,
      orderedShapes(state),
      state.viewport.scale,
      lastVertex ? [lastVertex] : undefined,
      {
        includeSegments: true,
        excludeSegmentShapeId: activeShapeId ?? undefined
      }
    );
    return {
      ...snapped,
      // A wall snap that moved the point off the constrained line wins.
      isConstrained:
        constrained.isConstrained &&
        snapped.x === candidate.x &&
        snapped.y === candidate.y,
      closeAt: null
    };
  };

  const frame = createFrameScheduler<{
    client: Point;
    shiftKey: boolean;
    altKey: boolean;
  }>(({ client, shiftKey, altKey }) => {
    if (!context || !lastVertex) return;
    const snapped = snapCandidate(context, client, shiftKey, altKey);
    const to = { x: snapped.x, y: snapped.y };
    context.overlay.set({
      draft: {
        kind: "polyline",
        points: [lastVertex],
        to,
        color: DEFAULT_WALL_COLOR,
        isConstrained: snapped.isConstrained,
        closeAt: snapped.closeAt ?? undefined
      },
      guides:
        snapped.alignX !== undefined || snapped.alignY !== undefined
          ? { x: snapped.alignX, y: snapped.alignY }
          : null,
      readout: {
        anchor: context.toWorld(client),
        lines: segmentReadout(
          lastVertex,
          to,
          context.store.getState().meta.pixelsPerMeter
        )
      }
    });
  });

  const stop = (closed = false) => {
    frame.cancel();
    if (closed && activeShapeId && context) {
      const id = activeShapeId;
      context.store.getState().commit((document) => {
        const path = document.shapes[id];
        if (path?.type !== "wall-path") return document;
        return updateShape(document, id, {
          closed: true,
          edges: [...path.edges, {}]
        } as Partial<WallPathShape>);
      });
    }
    lastVertex = null;
    activeShapeId = null;
    context?.overlay.reset();
  };

  return {
    kind: "wall-path",
    cursor: "crosshair",
    isBusy: () => lastVertex !== null,

    onPointerDown(toolContext, event) {
      context = toolContext;
      if (event.button === 2) {
        stop();
        return;
      }
      if (!isPrimaryButton(event)) return;
      const { store } = toolContext;
      const snapped = snapCandidate(
        toolContext,
        { x: event.clientX, y: event.clientY },
        event.shiftKey,
        event.altKey
      );
      const vertex = { x: snapped.x, y: snapped.y };
      const attach = snapped.segment;

      // Clicking near the first vertex closes the path.
      if (snapped.closeAt) {
        stop(true);
        return;
      }

      if (!lastVertex) {
        if (attach) {
          store
            .getState()
            .commit((document) =>
              splitHost(document, { ...attach, ...vertex })
            );
        }
        lastVertex = vertex;
        return;
      }

      if (!activeShapeId) {
        const path = createWallPath([lastVertex, vertex], { closed: false });
        store
          .getState()
          .commit((document) =>
            addShapes(
              attach ? splitHost(document, { ...attach, ...vertex }) : document,
              [path]
            )
          );
        activeShapeId = path.id;
        lastVertex = vertex;
        return;
      }

      const id = activeShapeId;
      store.getState().commit((document) => {
        const withHost = attach
          ? splitHost(document, { ...attach, ...vertex })
          : document;
        const path = withHost.shapes[id];
        if (path?.type !== "wall-path") return withHost;
        const vertices = [...path.vertices, vertex];
        return updateShape(withHost, id, {
          vertices,
          edges: [...path.edges, {}],
          ...computeWallPathBounds(vertices)
        } as Partial<WallPathShape>);
      });
      lastVertex = vertex;
    },

    onPointerMove(toolContext, event) {
      context = toolContext;
      if (!lastVertex) return;
      frame.schedule({
        client: { x: event.clientX, y: event.clientY },
        shiftKey: event.shiftKey,
        altKey: event.altKey
      });
    },

    onPointerUp() {},

    onDoubleClick(toolContext) {
      context = toolContext;
      if (activeShapeId) stop();
    },

    onKeyDown(toolContext, event) {
      if ((event.key === "Escape" || event.key === "Enter") && lastVertex) {
        context = toolContext;
        stop();
        return true;
      }
      return false;
    },

    cancel(toolContext) {
      context = toolContext;
      stop();
    }
  };
};
